/**
 * Provides a unified access to web-based key-value stores
 *
 * (cache, localStorage, sessionStorage, etc)
 */
export interface Store {
    save(key: string, x: Record<string, any>): Promise<void>;
    load(key: string): Promise<undefined | Record<string, any>>;
}

/** webcache-backed store */
export class WebCacheStore implements Store {
    /* webcache caches by URL rather than by a string-based key
     * and the value is stored in Response objects rather than a blob or a string
     */

    cache: Cache;

    constructor(cache: Cache) {
        this.cache = cache;
    }

    url(key: string) {
        return new URL(location.origin + '/' + encodeURIComponent(key));
    }

    save(key: string, x: Record<string, any>) {
        return this.cache.put(this.url(key), new Response(JSON.stringify(x)));
    }

    load(key: string) {
        return this.cache.match(this.url(key)).then(x => x?.json() as undefined | Record<string, any>);
    }
}

/** webstorage-backend store (localStorage + sessionStorage) */
export class WebStorageStore implements Store {
    storage: Storage;

    constructor(storage: Storage) {
        this.storage = storage;
    }

    save(key: string, x: Record<string, any>) {
        return Promise.resolve(
            this.storage.setItem(key, JSON.stringify(x))
        );
    }

    load(key: string) {
        return Promise.resolve(
            JSON.parse(this.storage.getItem(key) ?? 'null') as Record<string, any> | undefined
        );
    }
}

/** local-only store backed by a js Map object. only use as a last resort. */
export class MapStore implements Store {
    storage: Map<string, Record<string, any>>;

    constructor() {
        this.storage = new Map();
    }

    save(key: string, x: Record<string, any>) {
        this.storage.set(key, x);
        return Promise.resolve();
    }

    load(key: string) {
        return Promise.resolve(this.storage.get(key));
    }
}
